import * as Tabs from '@radix-ui/react-tabs';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import PANEL_CAPABILITIES from '../../shared/panelCapabilities';
import InspectionTabContainer from './InspectionTabContainer';
import FastInspectionTabContainer from './FastInspectionTabContainer';
import SoloInspectionTabContainer from './SoloInspectionTabContainer';
import { useInspectionSettings } from '../../contexts/InspectionSettingsContext';
import '../../styles/OversightTab.css';

const SECTIONS = [
  { value: 'council', label: 'شورای بازرسی', capability: 'oversight.council' },
  { value: 'strike', label: 'شورای ضربت', capability: 'oversight.strike' },
  { value: 'solo', label: 'ارتش تک‌نفره', capability: 'oversight.solo' },
];

// The same stand-alone inspection panels serve admins and supervisors.
export default function OversightTab({ role, basePath = '/admin/oversight' }) {
  const { tab, sub } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routed = basePath === '/admin/oversight' ? tab === 'oversight' : location.pathname.startsWith(`${basePath}/`) || location.pathname === basePath;
  const { effectiveSettings, concealModelIdentities } = useInspectionSettings();
  const visibleSections = SECTIONS.filter(({ capability }) => (
    PANEL_CAPABILITIES.canAccessPanelSection(role, capability)
  ));
  const visibleValues = new Set(visibleSections.map(({ value }) => value));
  if (visibleSections.length === 0) {
    return <div className="admin-subgroup" role="status">شما به هیچ بخش نظارت دسترسی ندارید.</div>;
  }
  const defaultSection = visibleSections[0].value;
  const activeSection = routed && visibleValues.has(sub) ? sub : defaultSection;
  if (routed && sub !== activeSection) return <Navigate to={`${basePath}/${activeSection}${location.search}`} replace />;

  return (
    <div className="admin-subgroup" dir="rtl">
      {/* dir="rtl" on Tabs.Root is what flips the visual flow - without it
          Radix renders LTR even when the surrounding div is dir="rtl", because
          Radix reads dir from its own props, not from DOM inheritance. */}
      <Tabs.Root value={activeSection} onValueChange={(value) => navigate(`${basePath}/${value}${location.search}`)} orientation="horizontal" dir="rtl">
        <Tabs.List className="admin-subnav" aria-label="بخش‌های نظارت">
          {visibleSections.map(({ value, label }) => (
            <Tabs.Trigger className="admin-subtab" value={value} key={value}>{label}</Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* forceMount on the three inspection subtabs preserves their polling
            loops and in-flight state across subtab switches. */}
        {visibleValues.has('council') && (
          <Tabs.Content value="council" forceMount className="admin-subtab-content">
            <InspectionTabContainer
              slots={effectiveSettings?.variants?.full}
              concealModelIdentities={concealModelIdentities}
            />
          </Tabs.Content>
        )}
        {visibleValues.has('strike') && (
          <Tabs.Content value="strike" forceMount className="admin-subtab-content">
            <FastInspectionTabContainer
              slots={effectiveSettings?.variants?.strike}
              concealModelIdentities={concealModelIdentities}
            />
          </Tabs.Content>
        )}
        {visibleValues.has('solo') && (
          <Tabs.Content value="solo" forceMount className="admin-subtab-content">
            <SoloInspectionTabContainer
              slots={effectiveSettings?.variants?.solo}
              concealModelIdentities={concealModelIdentities}
            />
          </Tabs.Content>
        )}
      </Tabs.Root>
    </div>
  );
}
